// REACT IMPORTS
import React, { useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { getAPICall } from "../../../config/apiCalls";

// MUI COMPONENTS
import {
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

// CONSTANTS
import { fmtHours } from "../../Projects/ProjectViewer/BudgetEditorTab";
import {
	SOURCE_PILLS,
	fmtDate,
	fmtMoney,
	pillStyle,
} from "./productLibraryUtils";
import { hubspotDealUrl, hubspotProductUrl } from "./productLibraryUtils";
import HelpLabel, { HELP } from "./HelpLabel";

const Stat = ({ label, help, value }) => (
	<div className="productLibraryStat">
		<span className="productLibraryFieldLabel">
			<HelpLabel label={label} help={help} />
		</span>
		<span className="productLibraryStatValue">{value}</span>
	</div>
);

// Copies one deal's wording; shows a check (or why not) for a moment after
const CopyWordingButton = ({ text }) => {
	const [status, setStatus] = useState(null); // "copied" | "failed"
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(text);
			setStatus("copied");
		} catch {
			setStatus("failed");
		}
		setTimeout(() => setStatus(null), 1500);
	};
	return (
		<Tooltip
			title={
				status === "copied"
					? "Copied"
					: status === "failed"
						? "Couldn't copy"
						: "Copy this deal's wording"
			}
			arrow
			placement="top"
		>
			<IconButton size="small" onClick={copy}>
				{status === "copied" ? (
					<CheckIcon fontSize="small" color="success" />
				) : (
					<ContentCopyIcon fontSize="small" />
				)}
			</IconButton>
		</Tooltip>
	);
};

/**
 * How a product has been used on proposals in the last year: totals and the
 * recent deals that used it
 * @param {Object|null} props.product - a Product Library row; open when set
 * @param {Function} props.onClose
 */
const ProductUsageDialog = ({ product, onClose }) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATE
	const [data, setData] = useState(null);
	const [error, setError] = useState("");

	useEffect(() => {
		setData(null);
		setError("");
		if (!product) return;
		getAPICall(
			instance,
			accounts[0],
			`/api/product-library/products/${product.id}`,
		)
			.then((res) => setData(res.data))
			.catch((err) =>
				setError(
					err?.response?.data?.error ||
						"Couldn't load this product's usage.",
				),
			);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product?.id]);

	const p = data?.product;
	const editedPct = p?.uses ? Math.round((p.editedCount / p.uses) * 100) : 0;

	return (
		<Dialog
			open={Boolean(product)}
			fullWidth
			maxWidth="md"
			onClose={onClose}
		>
			<DialogTitle id="editProductDialogTitle">
				<span className="productUsageTitle">
					Product Usage
					<span className="productUsageWindow">
						Proposal Pipeline deals created in the last{" "}
						{data?.usage?.windowDays ?? 365} days
					</span>
				</span>
			</DialogTitle>
			<DialogContent id="editProductDialogContent">
				<div className="productUsageNameField">
					<span className="productLibraryFieldLabel">Name</span>
					<span className="productUsageName">{product?.text}</span>
				</div>
				{error ? (
					<div className="productLibraryError">{error}</div>
				) : !data ? (
					<div className="productLibraryLoading">
						<CircularProgress color="primary" />
					</div>
				) : (
					<>
						<div className="productLibraryStatRow">
							<Stat
								label="Uses"
								help={HELP.uses}
								value={p.uses}
							/>
							<Stat
								label="Typical Hours"
								help={HELP.typicalHours}
								value={
									p.typicalHours !== null
										? fmtHours(p.typicalHours)
										: "—"
								}
							/>
							<Stat
								label="Hours Range"
								help={HELP.hoursRange}
								value={
									p.uses
										? `${fmtHours(p.minHours)}–${fmtHours(p.maxHours)}`
										: "—"
								}
							/>
							<Stat
								label="Wording Edited"
								help={HELP.editedPct}
								value={p.uses ? `${editedPct}%` : "—"}
							/>
							<Stat
								label="Last Used"
								help={HELP.lastUsed}
								value={fmtDate(p.lastUsed)}
							/>
						</div>

						{data.uses.length > 0 ? (
							<div className="productLibraryUsesTable">
								<span className="productLibraryFieldLabel">
									<HelpLabel
										label="Recent Deals"
										help={HELP.recentDeals}
									/>
									{p.uses > data.uses.length
										? ` (latest ${data.uses.length} of ${p.uses})`
										: ""}
								</span>
								<div className="productUsageTableScroll">
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell>Deal</TableCell>
												<TableCell>Created</TableCell>
												<TableCell align="right">
													Hours
												</TableCell>
												<TableCell align="right">
													Rate
												</TableCell>
												<TableCell>
													<HelpLabel
														label="Wording"
														help={HELP.wording}
													/>
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{data.uses.map((u, i) => (
												<TableRow
													key={`${u.dealId}-${i}`}
												>
													<TableCell>
														<a
															href={hubspotDealUrl(
																u.dealId,
															)}
															target="_blank"
															rel="noreferrer"
															className="productLibraryLink"
														>
															{u.dealName ||
																u.dealId}
														</a>
													</TableCell>
													<TableCell className="productLibraryNoWrap">
														{fmtDate(u.dealCreated)}
													</TableCell>
													<TableCell align="right">
														{fmtHours(u.hours)}
													</TableCell>
													<TableCell
														align="right"
														className="productLibraryNoWrap"
													>
														{u.rate !== null
															? fmtMoney(u.rate)
															: "—"}
													</TableCell>
													<TableCell>
														{u.edited ? (
															<Tooltip
																title={
																	u.editedText ||
																	""
																}
																arrow
																placement="left"
															>
																<span
																	style={{
																		...pillStyle(
																			SOURCE_PILLS.Edited,
																		),
																		cursor: "default",
																	}}
																>
																	Edited
																</span>
															</Tooltip>
														) : (
															<span
																style={pillStyle(
																	SOURCE_PILLS.Library,
																)}
															>
																Library
															</span>
														)}
														<CopyWordingButton
															text={
																u.editedText ||
																product?.text ||
																""
															}
														/>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							</div>
						) : (
							<div className="productLibraryMuted">
								Not used on any proposal in the last year.
							</div>
						)}
					</>
				)}

				<div className="dialogButtons">
					{product && (
						<Button
							variant="outlined"
							disableElevation
							href={hubspotProductUrl(product.id)}
							target="_blank"
							rel="noreferrer"
							endIcon={<OpenInNewIcon />}
							className="productUsageHubspotButton"
						>
							Open in HubSpot
						</Button>
					)}
					<Button
						variant="contained"
						disableElevation
						onClick={onClose}
					>
						Close
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ProductUsageDialog;
